import ManagerRepositories from '@/repository/manager';
import ShiftServiceRepositories from '@/repository/shift-service';
import instance from '@/repository/Repository';
import axios from 'axios';

// manager
import { OutsourcingRepository } from '@/model/repository/manager/outsourcing/OutsourcingRepository';
import { AbsentUseKindRepository } from '@/model/repository/manager/absentUseKind/AbsentUseKindRepository';
import { AdministratorsRepository } from '@/model/repository/manager/administrators/AdministratorsRepository';
import { AdministratorAttendancesRepository } from '@/model/repository/manager/administratorAttendances/AdministratorAttendancesRepository';
import { AgenciesRepository } from '@/model/repository/manager/agencies/AgenciesRepository';
import { AgreeToVideoStreamingTermsRepository } from '@/model/repository/manager/video/AgreeToVideoStreamingTermsRepository';
import { AuthRepository } from '@/model/repository/manager/auth/AuthRepository';
import { MembersRepository } from '@/model/repository/manager/members/MembersRepository';
import { MeRepository } from '@/model/repository/manager/me/MeRepository';
import { WhereaboutsCheckSettingRepository } from '@/model/repository/manager/whereaboutsCheck/WhereaboutsCheckSettingRepository';
import { TopicsRepository } from '@/model/repository/manager/topics/TopicsRepository';
import { TopicInsidesRepository } from '@/model/repository/manager/topicInsides/TopicInsidesRepository';
import { PlansRepository } from '@/model/repository/manager/plans/PlansRepository';
import { SchedulesRepository } from '@/model/repository/manager/schedules/SchedulesRepository';
import { CommentsRepository } from '@/model/repository/manager/comments/CommentsRepository';
import { CommunicationBookDisplayAdministratorRepository } from '@/model/repository/manager/communicationBookDisplayAdministrator/CommunicationBookDisplayAdministratorRepository';
import { ServiceGroupsRepository } from '@/model/repository/manager/serviceGroups/ServiceGroupsRepository';
import { CategoriesRepository } from '@/model/repository/manager/categories/CategoriesRepository';
import { MealPatternsRepository } from '@/model/repository/manager/mealPatterns/MealPatternsRepository';
import { ServiceOptionsRepository } from '@/model/repository/manager/serviceOptions/ServiceOptionsRepository';
import { GroupsRepository } from '@/model/repository/manager/groups/GroupsRepository';
import { SleepingRemarksRepository } from '@/model/repository/manager/sleepingRemarks/sleepingRemarks';
import { FacilityRepository } from '@/model/repository/manager/facility/FacilityRepository';
import { IAccidentReportRepository } from '@/model/repository/manager/accident/AccidentReportRepository';
import { BillConfigurationRepository } from '@/model/repository/manager/bill/BillConfigurationRepository';
import { BillsTotalRepository } from '@/model/repository/manager/bill/BillsTotalRepository';
import { SystemTroubleContactSettingRepository } from '@/model/repository/manager/communications/SystemTroubleContactSettingRepository';
import { PhotoRetailPriceRepository } from '@/model/repository/manager/parentSales/PhotoRetailPriceRepository';
import { FeatureRepository } from '@/model/repository/manager/feature/FeatureRepository';
import { ICustomPlanReportsRepository } from '@/model/repository/manager/customPlanReports/CustomPlanReportsRepository';
import { IFacilityCapacityRepository } from '@/model/repository/manager/facility/FacilityCapacityRepository';
import { BillsConfirmRepository } from '@/model/repository/manager/bill/BillsConfirmRepository';

// shift
import { AbsenceCategoriesRepository } from '@/model/repository/shift/AbsenceCategoriesRepository';
import { BackdoorRepository } from '@/model/repository/shift/BackdoorRepository';
import { ChildClassesRepository } from '@/model/repository/shift/ChildClassesRepository';
import { ChildGroupsRepository } from '@/model/repository/shift/ChildGroupsRepository';
import { ClassGroupWorkplaceRepository } from '@/model/repository/shift/ClassGroupWorkplaceRepository';
import { ClassWorkplaceRepository } from '@/model/repository/shift/ClassWorkplaceRepository';
import { ShiftDatesRepository } from '@/model/repository/shift/ShiftDatesRepository';
import { DutiesRepository } from '@/model/repository/shift/DutiesRepository';
import { DutyOrdersRepository } from '@/model/repository/shift/DutyOrdersRepository';
import { GroupWorkplaceRepository } from '@/model/repository/shift/GroupWorkplaceRepository';
import { SettingsRepository } from '@/model/repository/shift/SettingsRepository';
import { ShiftCategoriesOrderRepository } from '@/model/repository/shift/ShiftCategoriesOrderRepository';
import { ShiftCategoriesRepository } from '@/model/repository/shift/ShiftCategoriesRepository';
import { ShiftPatternsOrderRepository } from '@/model/repository/shift/ShiftPatternsOrderRepository';
import { ShiftPatternsRepository } from '@/model/repository/shift/ShiftPatternsRepository';
import { ShiftCountRepository } from '@/model/repository/shift/ShiftCountRepository';
import { SimpleSettingsRepository } from '@/model/repository/shift/SimpleSettingsRepository';
import { StaffingLevelsRepository } from '@/model/repository/shift/StaffingLevelsRepository';
import { WorkplacesRepository } from '@/model/repository/shift/WorkplacesRepository';
import { RoleRepository } from '@/model/repository/manager/role/RoleRepository';
import { PhotoPriceSettingRulesRepository } from '@/model/repository/manager/parentSales/PhotoPriceSettingRulesRepository';
import { ErrorPresenter } from '@/repository/errorPresenter/types';

const MANAGER_BASE_URL = `${process.env.MANAGER_API_URL}`;

const managerInstance = axios.create({
  withCredentials: true,
  baseURL: MANAGER_BASE_URL,
});

managerInstance.interceptors.request.use((request) => {
  try {
    request.headers['Codmon-Current-Page'] = window.location.href.split('#').pop();
  } catch (e) {
    request.headers['Codmon-Current-Page'] = '';
  }
  return request;
});

const managerRepositories = ManagerRepositories(managerInstance);
const shiftRepositories = ShiftServiceRepositories(instance);

export interface ManagerRepositories {
  absentUseKind: AbsentUseKindRepository;
  administrators: AdministratorsRepository;
  administratorAttendances: AdministratorAttendancesRepository;
  agencies: AgenciesRepository;
  agreeToVideoStreamingTerms: AgreeToVideoStreamingTermsRepository;
  auth: AuthRepository;
  categories: CategoriesRepository;
  mealPatterns: MealPatternsRepository;
  members: MembersRepository;
  me: MeRepository;
  whereaboutsCheckSetting: WhereaboutsCheckSettingRepository;
  topics: TopicsRepository;
  topicInsides: TopicInsidesRepository;
  plans: PlansRepository;
  schedules: SchedulesRepository;
  comments: CommentsRepository;
  communicationBookDisplayAdministrator: CommunicationBookDisplayAdministratorRepository;
  serviceGroups: ServiceGroupsRepository;
  serviceOptions: ServiceOptionsRepository;
  groups: GroupsRepository;
  sleepingRemarks: SleepingRemarksRepository;
  roles: RoleRepository;
  facility: FacilityRepository;
  outsourcing: OutsourcingRepository;
  accidentReport: IAccidentReportRepository;
  billConfiguration: BillConfigurationRepository;
  billsTotal: BillsTotalRepository;
  billsConfirm: BillsConfirmRepository;
  systemTroubleContactSetting: SystemTroubleContactSettingRepository;
  photoRetailPrice: PhotoRetailPriceRepository;
  photoPriceSettingRules: PhotoPriceSettingRulesRepository;
  feature: FeatureRepository;
  customPlanReports: ICustomPlanReportsRepository;
  whereaboutsCheckResult: (errorPresenter: ErrorPresenter) => WhereaboutsCheckResultRepository;
  whereaboutsCheckStatus: (errorPresenter: ErrorPresenter) => WhereaboutsCheckStatusRepository;
  facilityCapacity: IFacilityCapacityRepository;
}

export interface ShiftServiceRepositories {
  absenceCategories: AbsenceCategoriesRepository;
  backdoor: BackdoorRepository;
  childClasses: ChildClassesRepository;
  childGroups: ChildGroupsRepository;
  classGroupWorkplace: ClassGroupWorkplaceRepository;
  classWorkplace: ClassWorkplaceRepository;
  shiftDates: ShiftDatesRepository;
  duties: DutiesRepository;
  dutyOrders: DutyOrdersRepository;
  groupWorkplace: GroupWorkplaceRepository;
  settings: SettingsRepository;
  shiftCategories: ShiftCategoriesRepository;
  shiftCategoriesOrder: ShiftCategoriesOrderRepository;
  shiftPatterns: ShiftPatternsRepository;
  shiftCount: ShiftCountRepository;
  shiftPatternsOrders: ShiftPatternsOrderRepository;
  simpleSettings: SimpleSettingsRepository;
  staffingLevels: StaffingLevelsRepository;
  workplaces: WorkplacesRepository;
}

export interface ApiServices {
  manager: ManagerRepositories;
  shiftService: ShiftServiceRepositories;
}

const services: ApiServices = {
  manager: managerRepositories,
  shiftService: shiftRepositories,
};

export default services;
